import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/auth';

const card = "rounded-2xl border border-[#27272a] bg-[#18181b] shadow-xl";
const btn = "rounded-lg px-3 py-2 text-sm font-semibold transition";
const input = "w-full rounded-lg bg-[#09090b] border border-[#3f3f46] px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500";

export default function FormTemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [message, setMessage] = useState('');
  const [creating, setCreating] = useState(false);

  const loadTemplates = async () => {
    try {
      const res = await api.get('/forms/templates');
      setTemplates(Array.isArray(res.data) ? res.data : []);
      setMessage('');
    } catch (e) {
      console.error(e);
      setMessage('Could not load templates.');
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const deleteTemplate = async (template) => {
    const id = template.id || template._id;
    const name = template.name || 'this template';

    if (!id) return;

    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/forms/templates/${id}`);
      setTemplates(prev => prev.filter(t => (t.id || t._id) !== id));
      setMessage(`Deleted "${name}".`);
    } catch (e) {
      console.error(e);
      setMessage(e?.response?.data?.detail || 'Could not delete template.');
    }
  };

  const createTemplate = async () => {
    setCreating(true);
    try {
      const res = await api.post('/forms/templates', {
        name: 'New Operational Report',
        description: '',
        category: 'Operations',
        status: 'active',
        fields: [],
        assigned_roles: [],
        assigned_users: [],
        assigned_clients: [],
        assigned_sites: [],
      });

      const id = res.data?.id || res.data?._id;
      navigate(`/forms-studio/${id}`);
    } catch (e) {
      console.error(e);
      setMessage(e?.response?.data?.detail || 'Could not create template.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white p-4 md:p-6">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Forms Studio</h1>
          <p className="text-sm text-zinc-400">Create, manage and assign operational report templates.</p>
        </div>

        <button
          onClick={createTemplate}
          disabled={creating}
          className={`${btn} bg-blue-600 hover:bg-blue-500 disabled:opacity-50`}
        >
          {creating ? 'Creating...' : 'New Template'}
        </button>
      </div>

      {message ? (
        <div className="mb-4 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-200">
          {message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {templates.map((t) => {
          const id = t.id || t._id;
          return (
            <div key={id} className={`${card} p-5`}>
              <div className="mb-4">
                <h2 className="text-lg font-bold">{t.name}</h2>
                <p className="text-sm text-zinc-500">{t.category || 'Operations'}</p>
              </div>

              <div className="space-y-2 text-sm text-zinc-300 mb-5">
                <div>Fields: {(t.fields || []).length}</div>
                <div>Assigned officers: {(t.assigned_users || []).length}</div>
                <div>Status: {t.status || 'active'}</div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/forms-studio/${id}`)}
                  className={`${btn} bg-blue-600 hover:bg-blue-500`}
                >
                  Edit
                </button>
                <button
                  onClick={() => navigate(`/forms-studio/${id}?tab=assign`)}
                  className={`${btn} bg-zinc-800 hover:bg-zinc-700`}
                >
                  Assign
                </button>
                <button
                  onClick={() => deleteTemplate(t)}
                  className={`${btn} bg-red-600/20 border border-red-500/40 text-red-200 hover:bg-red-600/30`}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}

        {!templates.length ? (
          <div className="rounded-2xl border border-dashed border-[#3f3f46] p-10 text-center text-zinc-500">
            No templates yet. Create your first operational form.
          </div>
        ) : null}
      </div>
    </div>
  );
}
